// Statutory Demand service-pack builder — bundle case docs into one PDF
// and email it to a process server / messenger-at-arms from legal@.
import { desktopOnly } from "@/components/phone/DesktopOnlyNote";
import { useState, useEffect } from "react";
import { WizardDebugButton } from "@/components/ReportBugDialog";
import {
  Package, Loader2, CheckCircle2, XCircle, Upload, Trash2, Plus, Send, Mail, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  getPackFiles, uploadPackFile, sendServicePack, previewPackFile,
  listProcessServers, addProcessServer, deleteProcessServer, getLegalDocAddressBook,
} from "@/lib/api";
import { AddressFinder, postcodeCountry } from "./GenerateLegalDocWizard";

const fmtSize = (b) => !b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

function ServicePackDialogInner({ caseRow, kind = "sd2", preselectFileIds = [], debtorName = "", serviceAddress = "", onClose }) {
  const [files, setFiles]           = useState([]);
  const [selected, setSelected]     = useState(new Set(preselectFileIds));
  const [uploads, setUploads]       = useState([]);   // [{upload_id, name, size, mergeable}]
  const [uploading, setUploading]   = useState(false);
  const [servers, setServers]       = useState([]);
  const [serverId, setServerId]     = useState("");
  const [showAddServer, setShowAddServer] = useState(false);
  const [newServer, setNewServer]   = useState({ name: "", email: "", region: "" });
  const [debtor, setDebtor]         = useState(debtorName);
  const [address, setAddress]       = useState(serviceAddress);
  const [subject, setSubject]       = useState("");
  const [message, setMessage]       = useState("");
  const [chase, setChase]           = useState(true);
  const [chaseDays, setChaseDays]   = useState(7);
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(false);
  const [result, setResult]         = useState(null);
  const [addressBook, setAddressBook] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const [preview, setPreview]       = useState(null);

  const kindLabel = kind === "sd_scot" ? "Statutory Demand (Scotland)" : "Statutory Demand (England)";

  // Jurisdiction warning — flag when the service address is in the wrong country for this demand.
  const addrCountry = postcodeCountry(address);
  const jxWarning =
    kind === "sd2" && addrCountry === "scotland"
      ? "This is an England demand but the service address looks Scottish — should this be a Statutory Demand (Scotland)?"
      : kind === "sd_scot" && addrCountry === "england_wales"
        ? "This is a Scottish demand but the service address looks like England/Wales — should this be a Statutory Demand (England)?"
        : null;

  useEffect(() => {
    let alive = true;
    Promise.all([
      getPackFiles(caseRow.case_id).catch(() => ({ files: [] })),
      listProcessServers().catch(() => ({ servers: [] })),
    ]).then(([pf, ps]) => {
      if (!alive) return;
      setFiles(pf.files || []);
      setServers(ps.servers || []);
      if ((ps.servers || []).length === 1) setServerId(ps.servers[0].id);
      setLoading(false);
    });
    getLegalDocAddressBook(caseRow.case_id)
      .then(r => { if (alive) setAddressBook(r.candidates || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [caseRow.case_id]);

  const server = servers.find(s => s.id === serverId);

  const toggleFile = (key) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const handleUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    for (const f of picked) {
      try {
        const r = await uploadPackFile(caseRow.case_id, f);
        setUploads(prev => [...prev, r]);
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleAddServer = async () => {
    try {
      const r = await addProcessServer(newServer);
      setServers(prev => [...prev, r.server]);
      setServerId(r.server.id);
      setShowAddServer(false);
      setNewServer({ name: "", email: "", region: "" });
      toast.success(`${r.server.name} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const handleDeleteServer = async (id) => {
    if (!window.confirm("Remove this process server from the directory?")) return;
    try {
      await deleteProcessServer(id);
      setServers(prev => prev.filter(s => s.id !== id));
      if (serverId === id) setServerId("");
    } catch (e) { toast.error(e.message); }
  };

  const totalDocs = selected.size + uploads.length;

  const handlePreview = async (key, name) => {
    setPreviewing(key);
    try {
      const blob = await previewPackFile(caseRow.case_id, key);
      setPreview({ name, url: URL.createObjectURL(blob) });
    } catch (e) {
      toast.error(`Couldn't preview ${name}`);
    } finally {
      setPreviewing(null);
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleSend = async () => {
    if (!server) { toast.error("Pick a process server"); return; }
    if (!debtor.trim() || !address.trim()) { toast.error("Debtor name and service address are required"); return; }
    if (totalDocs === 0) { toast.error("Select at least one document"); return; }
    setSending(true);
    try {
      const r = await sendServicePack(caseRow.case_id, {
        kind,
        debtor_name: debtor.trim(),
        service_address: address.trim(),
        file_keys: Array.from(selected),
        upload_ids: uploads.map(u => u.upload_id),
        to_email: server.email,
        process_server_name: server.name,
        subject: subject.trim() || null,
        message: message.trim() || null,
        create_chase_event: chase,
        chase_days: Number(chaseDays) || 7,
        created_by: "Ops",
      });
      setResult(r);
      if (r.ok) {
        toast.success(`Service pack sent to ${server.name}`);
        window.dispatchEvent(new CustomEvent("portal:files-changed"));
      } else {
        toast.error("Pack email failed — see details");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="service-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-4 h-4 text-rose-600" />
            Service pack — {kindLabel} · {caseRow.customer_name}
            <WizardDebugButton className="ml-auto mr-6" />
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading case files…
          </div>
        ) : result ? (
          <div className="space-y-3">
            <div className={cn("border rounded p-3 text-xs flex items-start gap-2", result.ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200")} data-testid="service-pack-result">
              {result.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />}
              <div className="space-y-1">
                <div className="font-medium">{result.pack_filename} ({fmtSize(result.pack_size)})</div>
                <div>{result.email_sent ? `📧 Emailed to ${server?.name} (${server?.email}) from legal@` : "Email failed"}</div>
                <div>{result.onedrive_saved ? "💾 Saved to OneDrive Notices + case Files" : "OneDrive save failed"}</div>
                {result.calendar?.created > 0 && <div>📅 Chase diarised: {(result.calendar.events || []).map(e => e.date).join(", ")}</div>}
                {result.web_url && <a href={result.web_url} target="_blank" rel="noreferrer" className="text-blue-700 underline">Open pack</a>}
              </div>
            </div>
            {(result.warnings || []).map((w, i) => (
              <div key={i} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">⚠ {w}</div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4 items-start">
            {/* left column — documents */}
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">1 · Pack documents ({totalDocs} selected — merged into one PDF)</div>
              <div className="border rounded max-h-44 md:max-h-[48vh] overflow-y-auto divide-y" data-testid="pack-file-list">
                {files.length === 0 && <div className="p-2 text-[11px] text-muted-foreground">No case files yet — upload below.</div>}
                {files.map(f => (
                  <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" checked={selected.has(f.key)} onChange={() => toggleFile(f.key)} data-testid={`pack-file-${f.key}`} />
                    <span className="truncate flex-1">{f.name}</span>
                    <button
                      type="button" title="Preview document"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePreview(f.key, f.name); }}
                      className="text-slate-400 hover:text-blue-600 shrink-0"
                      data-testid={`pack-preview-${f.key}`}
                    >
                      {previewing === f.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                    <Badge variant="outline" className="text-[9px] shrink-0">{f.origin}</Badge>
                    {!f.mergeable && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">attach only</Badge>}
                    <span className="text-[10px] text-muted-foreground shrink-0">{fmtSize(f.size)}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <label className={cn("inline-flex items-center gap-1.5 text-[11px] border rounded px-2 py-1 cursor-pointer hover:bg-slate-50", uploading && "opacity-50 pointer-events-none")}>
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Upload extra files (ID, emails…)
                  <input type="file" multiple className="hidden" onChange={handleUpload} data-testid="pack-upload-input" />
                </label>
                {uploads.map((u, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] gap-1">
                    {u.name}
                    <button
                      type="button" title="Preview upload"
                      onClick={() => handlePreview(`up:${u.upload_id}`, u.name)}
                      className="text-slate-400 hover:text-blue-600"
                      data-testid={`pack-preview-upload-${i}`}
                    >
                      {previewing === `up:${u.upload_id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                    </button>
                    <button onClick={() => setUploads(prev => prev.filter((_, j) => j !== i))} className="text-red-500">×</button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* right column — server + details + diary */}
            <div className="space-y-4">
            {/* 2 — process server */}
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">2 · Process server / messenger-at-arms</div>
              <div className="space-y-1">
                {servers.map(s => (
                  <label key={s.id} className={cn("flex items-center gap-2 border rounded px-2 py-1.5 text-xs cursor-pointer", serverId === s.id ? "border-rose-300 bg-rose-50" : "hover:bg-slate-50")} data-testid={`process-server-${s.id}`}>
                    <input type="radio" name="ps" checked={serverId === s.id} onChange={() => setServerId(s.id)} />
                    <span className="font-medium">{s.name}</span>
                    <span className="text-muted-foreground">{s.email}</span>
                    {s.region && <Badge variant="outline" className="text-[9px]">{s.region}</Badge>}
                    <button className="ml-auto text-slate-400 hover:text-red-600" onClick={(e) => { e.preventDefault(); handleDeleteServer(s.id); }} data-testid={`delete-ps-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </label>
                ))}
                {showAddServer ? (
                  <div className="border rounded p-2 space-y-1.5 bg-slate-50">
                    <div className="grid grid-cols-3 gap-1.5">
                      <Input placeholder="Name" value={newServer.name} onChange={e => setNewServer(p => ({ ...p, name: e.target.value }))} className="h-7 text-xs" data-testid="new-ps-name" />
                      <Input placeholder="email@…" value={newServer.email} onChange={e => setNewServer(p => ({ ...p, email: e.target.value }))} className="h-7 text-xs" data-testid="new-ps-email" />
                      <Input placeholder="Region (optional)" value={newServer.region} onChange={e => setNewServer(p => ({ ...p, region: e.target.value }))} className="h-7 text-xs" />
                    </div>
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-6 text-[11px]" onClick={handleAddServer} data-testid="save-ps-btn">Save</Button>
                      <Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setShowAddServer(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={() => setShowAddServer(true)} data-testid="add-ps-btn">
                    <Plus className="w-3 h-3 mr-1" />Add process server
                  </Button>
                )}
              </div>
            </div>

            {/* 3 — service details + email */}
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">3 · Service details & email (sent from legal@simplyfunded.co.uk)</div>
              {jxWarning && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 mb-1.5" data-testid="pack-jurisdiction-warning">
                  ⚠ {jxWarning}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-slate-700">Debtor name</label>
                  <Input value={debtor} onChange={e => setDebtor(e.target.value)} placeholder="e.g. John Smith" className="h-8 text-xs" data-testid="pack-debtor" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-700">Service address</label>
                  <div className="flex gap-1">
                    <Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Where to serve the debtor" className="h-8 text-xs flex-1" data-testid="pack-address" />
                    <AddressFinder addressBook={addressBook} onPick={(a) => setAddress(a.full || a.line || "")} testId="pack-address-finder" />
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] font-medium text-slate-700">Subject <span className="text-muted-foreground font-normal">(blank = auto)</span></label>
                  <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder={`${kindLabel} for personal service — ${debtor || "debtor"} — ${caseRow.loan_application_id || ""}`} className="h-8 text-xs" data-testid="pack-subject" />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] font-medium text-slate-700">Message <span className="text-muted-foreground font-normal">(blank = standard instruction text)</span></label>
                  <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Standard service instructions will be used if left blank" className="text-xs min-h-[56px]" data-testid="pack-message" />
                </div>
              </div>
            </div>

            {/* 4 — diary */}
            <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer border rounded p-2 bg-blue-50/50">
              <input type="checkbox" checked={chase} onChange={e => setChase(e.target.checked)} data-testid="pack-chase-event" />
              <span className="font-medium">Diary "Chase Process Server / Messenger-at-Arms"</span>
              <span className="text-muted-foreground">in</span>
              <Input type="number" min="1" max="60" value={chaseDays} onChange={e => setChaseDays(e.target.value)} className="h-6 w-14 text-xs" data-testid="pack-chase-days" />
              <span className="text-muted-foreground">days</span>
            </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="pack-close">{result ? "Done" : "Cancel"}</Button>
          {!result && !loading && (
            <Button onClick={handleSend} disabled={sending || uploading} data-testid="pack-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              {sending ? "Building & sending…" : `Send pack (${totalDocs})`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      {preview && (
        <Dialog open onOpenChange={(v) => !v && closePreview()}>
          <DialogContent className="max-w-4xl h-[85vh] p-3 flex flex-col" aria-describedby={undefined} data-testid="pack-preview-dialog">
            <DialogHeader className="pb-1">
              <DialogTitle className="text-sm truncate flex items-center gap-2">
                <Eye className="w-3.5 h-3.5 text-slate-500 shrink-0" />{preview.name}
              </DialogTitle>
            </DialogHeader>
            <iframe src={preview.url} title={preview.name} className="flex-1 w-full border rounded bg-slate-100" data-testid="pack-preview-frame" />
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

export const ServicePackDialog = desktopOnly(ServicePackDialogInner, "The service pack");
